import { Flag, GraphFlag, GraphPoint, GraphPointRR, PPG } from 'models/measurement';

import HighchartsMore from 'highcharts/highcharts-more';
import Dumbbell from 'highcharts/modules/dumbbell';
import Lollipop from 'highcharts/modules/lollipop';
import Highcharts from 'highcharts';

const badFlags = ['quality_flag', 'quality', 'time_flag', 'time', 'nondefined', 'uncertain'];

const getMedian = (sortedValues: number[]): number => {
  const middle = Math.floor(sortedValues.length / 2);
  return sortedValues.length % 2 === 0 ? (sortedValues[middle - 1] + sortedValues[middle]) / 2 : sortedValues[middle];
};

export const getMedianAbsoluteDeviation = (values: number[]): number => {
  if (!values.length) return 0;

  const median = getMedian([...values].sort((a, b) => a - b));
  const deviations = values.map((value) => Math.abs(value - median)).sort((a, b) => a - b);

  return getMedian(deviations);
};

export const initHighcharts = () => {
  (HighchartsMore as any)(Highcharts);
  (Dumbbell as any)(Highcharts);
  (Lollipop as any)(Highcharts);
};

export function getGraphData(ppgData?: PPG, peaksData?: number[], flagsData?: Flag[]) {
  const ppg: GraphPoint[] = [];
  const peaks: GraphPoint[] = [];
  const flags: GraphFlag[] = [];
  const rr: GraphPointRR[] = [];
  let timePeak: number;

  // Round off all peak data to multiples of ppg frequency
  const roundedPeaksData = peaksData?.map((point) => Math.round(point)) || [];
  const badFlagSignal = ppgData && ppgData.signal ? Array(ppgData.signal.length) : [];

  if (flagsData !== undefined && flagsData !== null && flagsData.length > 0) {
    flagsData.forEach((val) => {
      if (badFlags.indexOf(val.name) > -1) {
        if (val.start != null) {
          val.start.forEach((point: number, index: number) => {
            for (let i = point; i <= val.end[index] - 1; i += 1) {
              badFlagSignal[i] = true;
            }
          });
        }
      }
    });
  }

  let badFlagInterval = false;

  // Find the matching peaks and construct array
  if (ppgData && ppgData.signal) {
    ppgData.signal.forEach((point: number, index: number) => {
      const time = index * (1 / ppgData.frequency);
      ppg.push([time, point]);

      if (timePeak && !badFlagInterval) {
        if (badFlagSignal[index]) {
          badFlagInterval = true;
        }
      }
      if (roundedPeaksData?.indexOf(index) > -1) {
        peaks.push([time, point]);

        if (timePeak) {
          const rrVal = Math.abs(time * 1000 - timePeak * 1000);
          rr.push([Math.round(rrVal), badFlagInterval]);

          if (badFlagSignal[index]) {
            badFlagInterval = true;
          } else {
            badFlagInterval = false;
          }

          timePeak = time;
        } else {
          timePeak = time;
          if (badFlagSignal[index]) {
            badFlagInterval = true;
          }
        }
      }
    });
  }

  const flagsNames: string[] = [];
  // Get from-to points for the flags
  if (flagsData !== undefined && flagsData !== null && flagsData.length > 0) {
    let start = null;
    const qualityStart: number[] = [];
    const qualityEnd: number[] = [];

    if (ppgData) {
      for (let i = 0; i <= ppgData.signal.length; i += 1) {
        if (badFlagSignal[i]) {
          if (start == null) {
            start = i;
          }
        } else if (start != null) {
          qualityStart.push(start);
          qualityEnd.push((i -= 1));
          start = null;
        }
      }
    }

    if (qualityStart.length > 0) {
      qualityStart.forEach((pnt: number, index: number) => {
        const startIndex = pnt === 0 ? pnt : pnt - 1;
        flags.push({ from: ppg[startIndex][0], to: ppg[qualityEnd[index]][0], color: 'var(--mantine-color-gray-5)' });
      });
      flagsNames.push('quality');
    }
  }

  return { ppg, peaks, rr, flags, flagsNames };
}
